import React, { useEffect, useRef } from 'react';
import { ChevronDownIcon } from '../Icons/ChevronDownIcon/ChevronDownIcon';
import { useMenu } from '@interstate/components/Menu';
import type { ILinkNavViewItem } from '@makemydeal/dr-sales-checkin-types';

import { StyledShareButton, DropdownWrapper, DropdownPanel, DropdownItem } from './SendLinkDropdown.style';

/**
 * Share dropdown. Config-driven (US2055174): the menu items and their behaviour
 * come from `items` (the resolved DROPDOWN entities passed in by the parent) — each
 * item carries its own `clickAction` command whose `canExecute` reflects the SCI
 * share rules. Items that aren't currently available render disabled (not hidden);
 * the component hides itself only when there are no items at all.
 */
const SendLinkDropdown: React.FC<{
    items: ILinkNavViewItem[];
    buttonLabel?: string;
}> = ({ items, buttonLabel = 'Share' }) => {
    const menuProps = useMenu();
    const wrapperRef = useRef<HTMLDivElement>(null);

    const isOpen = !!menuProps.referenceProps.open;

    // Close dropdown when user clicks outside the wrapper
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                menuProps.referenceProps.onClose?.();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, menuProps.referenceProps]);

    if (items.length === 0) {
        return null;
    }

    return (
        <DropdownWrapper ref={wrapperRef}>
            <StyledShareButton
                data-testid="send-link-dropdown-button"
                buttonStyle="tertiary"
                size="small"
                onClick={menuProps.onClick}
            >
                {buttonLabel}
                <ChevronDownIcon />
            </StyledShareButton>
            {isOpen && (
                <DropdownPanel>
                    {items.map((item) => {
                        const disabled = !item.clickAction.canExecute;
                        return (
                            <DropdownItem
                                key={item.key}
                                data-testid={`menu-option-${item.key}`}
                                disabled={disabled}
                                aria-disabled={disabled || undefined}
                                onClick={() => {
                                    menuProps.setOpen(false);
                                    item.clickAction.execute();
                                }}
                            >
                                {item.label}
                            </DropdownItem>
                        );
                    })}
                </DropdownPanel>
            )}
        </DropdownWrapper>
    );
};

export default SendLinkDropdown;
